import { useContext } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Bed, Bath, Car, Maximize } from "lucide-react";
import { AppContext } from "@/App";
import { motion } from "framer-motion";
import OptimizedImage from "./OptimizedImage";
import FavoriteButton from "./FavoriteButton";
import CompareButton from "./CompareButton";
import { ViewCountBadge } from "./PropertyViewCounter";

const PropertyCard = ({ property, index = 0, compact = false }) => {
  const { t } = useTranslation();
  const { formatPrice, formatArea } = useContext(AppContext);

  const images = property.images || [];
  const mainImage = images.find(img => img.is_main) || images[0] || { url: "https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg", alt: "Property" };
  const imageUrl = mainImage.url;

  // Compact version for map sidebar
  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
      >
        <Link 
          to={`/propiedad/${property.slug || property.id}`}
          className="flex gap-4 p-3 bg-[#121212] border border-white/5 rounded-lg hover:border-[#D4AF37]/50 transition-all group"
          data-testid={`property-card-compact-${property.id}`}
        >
          {/* Thumbnail */}
          <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
            <OptimizedImage 
              src={imageUrl} 
              alt={property.name}
              className="w-full h-full group-hover:scale-110 transition-transform duration-500"
            />
          </div>
          
          {/* Info */}
          <div className="flex-1 min-w-0 flex flex-col justify-between">
            <div>
              <span className="text-xs text-[#D4AF37] uppercase tracking-wider">
                {property.property_type}
              </span>
              <h3 className="text-white font-medium text-sm line-clamp-1 group-hover:text-[#D4AF37] transition-colors">
                {property.name}
              </h3>
              <p className="text-[#A3A3A3] text-xs flex items-center gap-1 mt-1">
                <MapPin size={10} />
                <span className="line-clamp-1">{property.zone}</span>
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <p className="font-mono text-[#D4AF37] font-medium text-sm">
                {formatPrice(property.price_usd)}
              </p>
              <div className="flex gap-2 text-[#A3A3A3] text-xs">
                {property.bedrooms > 0 && (
                  <span className="flex items-center gap-1">
                    <Bed size={10} /> {property.bedrooms}
                  </span>
                )}
                {property.bathrooms > 0 && (
                  <span className="flex items-center gap-1">
                    <Bath size={10} /> {property.bathrooms}
                  </span>
                )}
              </div>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Link 
        to={`/propiedad/${property.slug || property.id}`}
        className="block property-card-container group"
        data-testid={`property-card-${property.id}`}
      >
        {/* Image */}
        <div className="property-card-image relative">
          <OptimizedImage 
            src={imageUrl} 
            alt={property.name}
            className="w-full h-full"
            priority={index < 4} // Load first 4 images immediately
          />
          
          {/* View Count Badge */}
          <ViewCountBadge propertyId={property.id} />
          
          {/* Badge */}
          <span className={`property-badge ${property.transaction_type === "Venta" ? "sale" : "rent"}`}>
            {property.transaction_type === "Venta" ? t('badges.forSale') : t('badges.forRent')}
          </span>

          {/* Featured badge */}
          {property.is_featured && (
            <span className="absolute top-4 right-12 bg-[#D4AF37] text-black text-xs font-medium px-2 py-1 uppercase tracking-wider">
              {t('badges.featured')}
            </span>
          )}
          
          {/* Favorite Button */}
          <FavoriteButton 
            property={property} 
            size="small"
            className="absolute top-3 right-3"
          />
          
          {/* Compare Button */}
          <CompareButton
            property={property}
            size="small"
            className="absolute top-3 right-12"
          />

          {/* Hover overlay with quick stats */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end">
            <div className="p-4 w-full flex gap-4 text-white text-sm">
              {property.bedrooms > 0 && (
                <span className="flex items-center gap-1">
                  <Bed size={14} />
                  {property.bedrooms}
                </span>
              )}
              {property.bathrooms > 0 && (
                <span className="flex items-center gap-1">
                  <Bath size={14} />
                  {property.bathrooms}
                </span>
              )}
              {property.parking_spaces > 0 && (
                <span className="flex items-center gap-1">
                  <Car size={14} />
                  {property.parking_spaces}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Maximize size={14} />
                {formatArea(property.area_m2)}
              </span>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="p-5">
          {/* Property Type */}
          <span className="text-xs text-[#D4AF37] uppercase tracking-wider font-medium">
            {t(`propertyTypes.${property.property_type}`, property.property_type)}
          </span>

          {/* Name */}
          <h3 className="mt-2 text-lg font-semibold text-white group-hover:text-[#D4AF37] transition-colors line-clamp-1">
            {property.name}
          </h3>

          {/* Location */}
          <p className="mt-1 flex items-center gap-1 text-sm text-[#A3A3A3]">
            <MapPin size={14} className="flex-shrink-0" />
            <span className="line-clamp-1">{property.department}, {property.zone}</span>
          </p>

          {/* Price */}
          <div className="mt-4 pt-4 border-t border-white/5">
            <p className="font-mono text-xl text-[#D4AF37] font-medium">
              {formatPrice(property.price_usd)}
              {property.transaction_type === "Alquiler" && (
                <span className="text-sm text-[#A3A3A3] font-sans"> /{t('common.month')}</span>
              )}
            </p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default PropertyCard;
